import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Loader } from '@googlemaps/js-api-loader';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  PanAppState,
  PlatformService,
  UserState,
  jwtDecode,
} from '@panamax/app-state';
import { OrderDownloadService } from '@shared/services/file-download/order-download.service';
import { Customer } from '@usf/customer-types/customer';
import {
  MyOrdersStoreService,
  Order,
  OrderETAMessageType,
  OrderHeader,
  OrderItem,
  OrderReviewStoreService,
  OrderStatus,
  orderSelectors,
} from '@usf/ngrx-order';
import { DeliveryDetails } from '@usf/order-types';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  of,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { DealsService } from 'src/app/deals/services/deals.service';
import { CustomerStoreService } from 'src/app/ngrx-customer/services';
import { Crumb } from 'src/app/shared/components/breadcrumbs/breadcrumbs.component';
import { OrderStatusDetails } from 'src/app/shared/constants/order-status-title.enum';
import { PageTitle } from 'src/app/shared/constants/page-titles.enum';
import { FEATURES } from 'src/app/shared/constants/splitio-features';
import { WMTService } from 'src/app/shared/services/wmt/wmt.service';
import { environment } from 'src/environments/environment';
import { SimpleActionModalComponent } from '../../../shared/components/simple-action-modal/simple-action-modal.component';
import { PATHS } from '../../../shared/constants/paths';
import { ModalService } from '../../../shared/services/modal/modal.service';
import {
  ExternalTrackingInformation,
  OrderTrackingGroup,
  SubmittedOrderViewModel,
} from '../../models/submitted-order-view-model';
import { OrderService } from '../../services/order.service';
import { OrderSubmittedAnalyticsService } from './tracking/analytics/order-submitted-analytics.service';
import { OrderSubmittedTracingService } from './tracking/tracing/order-submitted-tracing.service';
import { OrderSyncingComponent } from '@shared/components/popovers/order-syncing/order-syncing.component';
import { Product } from '@shared/models/product.model';
import { DownloadOrdersEvent } from '@order/models/download-orders-event';
import { Store } from '@ngrx/store';
import { State } from '@app/store';
import { MessageHandlerService } from '@shared/services/message/message-handler.service';
import { productMatchesKeyword } from '@app/lists/shared/list-detail-management/selectors/helpers/list-detail-management.selectors.helper';
import { SortDirections } from '@shared/components/sortable-section/models/sortable-section.model';
import { ShipToModalSelections } from '@order/models/order-view-model';
import { OrderConversionService } from '@order/services/order-conversion.service';
import { ConvertToWillCallAnalyticsService } from './will-call/analytics/convert-to-will-call-analytics.service';
import { ConvertToWillCallTracingService } from './will-call/tracing/convert-to-will-call-tracing.service';
import { WillCallConfirmModalComponent } from '../../../shared/components/will-call-confirm-modal/will-call-confirm-modal.component';
import { RecoveryRequest } from '@usf/list-types';
import { ServiceRequestService } from '@service-request/services/service-request.service';
import { WillCallEvent } from '../../models/will-call-event';
import { SubmittedOrderInfoCardComponent } from '@order/components/order-submitted/submitted-order-info-card/submitted-order-info-card.component';
import { OrderAnalyticsService } from '../../tracking/analytics/order-analytics.service';
import { isProntoDay } from '@shared/helpers/pronto.helpers';
import { PRONTO_KEYS } from '@shared/constants/pronto-constants';

@Component({
  selector: 'app-order-submitted.page',
  templateUrl: './order-submitted.page.html',
  styleUrls: ['./order-submitted.page.scss'],
})
export class OrderSubmittedPage {
  @ViewChild(SubmittedOrderInfoCardComponent)
  submittedOrderInfoCard: SubmittedOrderInfoCardComponent;
  @ViewChild('map') mapEl: any;
  applyFilter: boolean;
  filterSelection = new BehaviorSubject<boolean>(false);
  sortFilterCount: number;
  customer$: Observable<Customer>;
  customerSub$: Subscription;
  getDeliveryDetails$: Subscription;
  deliveryDetails: DeliveryDetails;
  orderHasDeliveryDetails: boolean;
  deliveryETA: string;
  actualDeliveryDate: Date;
  selectedSubmittedOrderId: string;
  submittedOrderVm$: Observable<SubmittedOrderViewModel>;
  submittedOrderVmSub$: Subscription;
  submittedOrderVmPageLoadedSub$: Subscription;
  submittedOrderVm: SubmittedOrderViewModel;
  breadcrumbs: Crumb[];
  confirmModalOpen = false;
  orderStatusDetails: OrderStatusDetails;
  map: google.maps.Map;
  closedPanels: string[] = [];
  trackingInfoModal: HTMLIonModalElement;
  convertToWillCallFeatureFlag = FEATURES.split_global_convert_to_willcall.name;
  orderDetailsDownloadButtonFeatureFlag =
    FEATURES.split_global_order_details_download.name;
  orderDetailsPageRefresherFlag =
    FEATURES.split_global_order_detail_refresher.name;
  dealsProductsExistSub$: Subscription;
  isDealsAvailable: boolean;
  value = 'accordionRow';
  expanded = true;
  customerTimeZone = 'CT';
  autosubDisplayEnabled;
  downloadButtonDisplayEnabled: boolean;
  hideEtaGlobalFeatureFlag: boolean;
  substituteProducts: OrderItem[];
  showDownloadOrderModal$ = new BehaviorSubject<boolean>(false);
  showSortModal$ = new BehaviorSubject<boolean>(false);
  showSubmittedOrderActionSheet$ = new BehaviorSubject<boolean>(false);
  showShipToModal$ = new BehaviorSubject<boolean>(false);
  showRecoveryModal$ = new BehaviorSubject<boolean>(false);
  bannerStyle = 'my-deals';
  dealsUrl = ['/deals'];
  isInternalUser: boolean;
  isAllCoordinatesValid = false;
  readonly orderETAMessageType = OrderETAMessageType;
  readonly orderStatusEnum = OrderStatus;
  searchKey = new BehaviorSubject<string>('');
  filteredOrderItems$: Observable<OrderItem[]>;
  orderTrackingGroups$: Observable<OrderTrackingGroup>;
  sortByReserved = new BehaviorSubject<SortDirections>(null);
  shipToInFlight$ = this.orderConversionService.isShipToInFlight();
  willCallInFlight$ = this.orderConversionService.isWillCallInFlight();
  showWillCallModal$ = new BehaviorSubject<boolean>(false);
  showEmail$: Observable<boolean>;
  superUserShipToCustomer$: Observable<Customer>;
  superUserShipToCustomer: Customer;
  hasRetrievedShipToCustomer = false;
  hideEtaGlobalFeatureFlag$: Observable<boolean>;
  issueOptions$ = this.orderService.getRecoveryOrderRequestReasonIssueDetails();
  originType = '';
  isProntoDeliveryDate: boolean;
  isSuperUserProntoCustomer: boolean;
  isWmtTimezoneFeatureFlag: boolean;

  constructor(
    readonly panAppState: PanAppState,
    readonly platformService: PlatformService,
    private customerStoreService: CustomerStoreService,
    private route: ActivatedRoute,
    private orderService: OrderService,
    private myOrderStoreService: MyOrdersStoreService,
    private modalController: ModalController,
    private translate: TranslateService,
    private router: Router,
    private wmtService: WMTService,
    private orderSubmittedAnalyticsService: OrderSubmittedAnalyticsService,
    private orderSubmittedTracingService: OrderSubmittedTracingService,
    private convertToWillCallAnalyticsService: ConvertToWillCallAnalyticsService,
    private convertToWillCallTracingService: ConvertToWillCallTracingService,
    private modalService: ModalService,
    private dealsService: DealsService,
    private orderDownloadService: OrderDownloadService,
    private store: Store<State>,
    private messageHandlerService: MessageHandlerService,
    private orderConversionService: OrderConversionService,
    private serviceRequestService: ServiceRequestService,
    private orderReviewStoreService: OrderReviewStoreService,
  ) {}

  ionViewWillEnter(): void {
    this.selectedSubmittedOrderId = this.route?.snapshot?.params?.orderId;
    this.originType = this.route?.snapshot?.queryParams?.originType;
    combineLatest([
      this.panAppState.feature$([
        FEATURES.split_global_autosub_review_order.name,
      ]),
      this.panAppState.feature$([
        FEATURES.split_global_order_details_download.name,
      ]),
      this.panAppState.feature$([FEATURES.split_global_order_hide_eta.name]),
      this.panAppState.feature$([
        FEATURES.split_dynamic_order_wmt_timezone.name,
      ]),
    ])
      .pipe(take(1))
      .subscribe(
        ([
          autosubFlag,
          downloadFlag,
          hideEtaFeatureFlag,
          wmtTimezoneFeatureFlag,
        ]) => {
          this.autosubDisplayEnabled = autosubFlag;
          this.downloadButtonDisplayEnabled = downloadFlag;
          this.hideEtaGlobalFeatureFlag = hideEtaFeatureFlag;
          this.isWmtTimezoneFeatureFlag = wmtTimezoneFeatureFlag;
        },
      );

    this.panAppState?.setPageTitle(PageTitle?.submittedOrder);
    if (this.selectedSubmittedOrderId) {
      this.submittedOrderVm$ = this.orderService
        .loadSubmittedOrderViewModelById(this.selectedSubmittedOrderId)
        .pipe(
          filter(vm => vm?.customerReady),
          filter(view =>
            view?.submittedOrderById?.orderItems?.every(
              item => !!view?.products?.get(item?.productNumber),
            ),
          ),
        );

      this.submittedOrderVmSub$ = this.submittedOrderVm$.subscribe(
        (vm: SubmittedOrderViewModel) => {
          this.submittedOrderVm = vm;

          this.substituteProducts = vm?.submittedOrderById?.orderItems?.filter(
            oi =>
              !!oi?.substituteFor &&
              !!vm?.autoSubItems.get(oi.substituteFor)?.orderItem,
          );
          this.customer$ = this.customerStoreService?.loadCustomerWithNumber$(
            this.submittedOrderVm?.submittedOrderById?.orderHeader
              ?.customerNumber,
          );

          this.orderStatusDetails = vm?.orderStatusDetails;

          this.setIsInternalUser();
          this.getDeliveryDetails();
          this.checkForProntoOrder();

          const pageTitle =
            'Order #' +
            this.submittedOrderVm?.submittedOrderById?.orderHeader
              ?.tandemOrderNumber;
          this.panAppState?.setPageTitle(pageTitle);
          this.breadcrumbs = [
            {
              label: 'i18n.breadcrumbs.home',
              url: '/desktop/home',
              active: false,
            },
            {
              label: 'i18n.breadcrumbs.myOrders',
              url: '/desktop/my-orders-view',
              active: false,
            },
            {
              label: pageTitle,
              url: '',
              active: true,
            },
          ];
        },
      );

      this.filteredOrderItems$ = combineLatest([
        this.searchKey,
        this.submittedOrderVm$.pipe(
          distinctUntilChanged(
            (prevVm, currVm) =>
              prevVm?.submittedOrderById?.orderItems ===
              currVm?.submittedOrderById?.orderItems,
          ),
        ),
        this.sortByReserved,
      ]).pipe(
        map(([searchKey, vm, sortingMethod]) => {
          let filteredItems = vm?.submittedOrderById?.orderItems?.filter(
            item => {
              const product = vm?.products?.get(item?.productNumber);
              return productMatchesKeyword(
                searchKey,
                product?.summary,
                product?.alternative,
              );
            },
          );

          if (this.applyFilter) {
            filteredItems = this.filterOrderItemsByUnreserved(filteredItems);
          }

          if (!!sortingMethod) {
            return this.sortOrderItemsByReserved(filteredItems, sortingMethod);
          } else {
            return filteredItems;
          }
        }),
      );

      this.orderTrackingGroups$ = combineLatest([
        this.searchKey,
        this.orderService.getOrderTrackingGroups(this.selectedSubmittedOrderId),
        this.submittedOrderVm$,
      ]).pipe(
        map(([searchKey, orderTrackingGroup, vm]) => {
          if (!orderTrackingGroup) return {};
          const newOrderTrackingGroup = JSON.parse(
            JSON.stringify(orderTrackingGroup),
          );

          Object.keys(orderTrackingGroup).forEach(key => {
            newOrderTrackingGroup[key] = orderTrackingGroup[key]?.filter(
              ({ orderItem }) => {
                const product = vm?.products?.get(orderItem.productNumber);
                return productMatchesKeyword(
                  searchKey,
                  product?.summary,
                  product?.alternative,
                );
              },
            );
          });
          return newOrderTrackingGroup;
        }),
      );
      this.calculateIfDealsAreAvailable();
    }

    this.getSuperUserShipToCustomerIfNeeded(false);

    // Reset needed if the last will call websocket wasn't received
    this.orderService.setWillCallInFlight(false);
    this.setShowEmail();
  }

  ionViewDidEnter() {
    this.submittedOrderVmPageLoadedSub$ = this.orderService
      ?.loadSubmittedOrderViewModelById(this.selectedSubmittedOrderId)
      ?.pipe(
        filter(view =>
          view?.submittedOrderById?.orderItems?.every(
            item => !!view?.products?.get(item?.productNumber),
          ),
        ),
        take(1),
      )
      .subscribe((vm: SubmittedOrderViewModel) => {
        this.submittedOrderVm = vm;
        this.substituteProducts = vm?.submittedOrderById?.orderItems?.filter(
          oi =>
            !!oi?.substituteFor &&
            !!vm?.autoSubItems.get(oi.substituteFor)?.orderItem,
        );

        const analyticsOrderId = OrderAnalyticsService.getAnalyticsOrderId(
          vm?.submittedOrderById?.orderHeader,
        );
        this.orderSubmittedAnalyticsService.trackPageLoad(
          analyticsOrderId,
          vm?.submittedOrderById?.orderHeader?.orderStatus,
          vm?.submittedOrderById?.orderHeader?.divisionNumber,
          vm?.submittedOrderById?.orderItems,
          vm?.products,
          this.orderHasDeliveryDetails,
        );
        this.orderSubmittedTracingService.submittedOrderDetailsPageLoad(
          vm?.submittedOrderById.orderHeader,
          vm?.submittedOrderById?.orderItems,
          vm?.products,
        );
        if (this.isInternalUser) {
          this.orderReviewStoreService.updateOrderReviewPreferences(
            this.selectedSubmittedOrderId,
          );
        }
      });
    if (this.originType && this.originType === 'email-alerts-whscuts') {
      this.orderSubmittedAnalyticsService.trackOrderSubmittedDeeplink();
    }
  }

  calculateIfDealsAreAvailable() {
    this.dealsProductsExistSub$ = this.dealsService
      ?.selectDealsProductsExist$()
      ?.subscribe(dealsProductsExist => {
        this.isDealsAvailable = dealsProductsExist;
      });
  }

  ionViewWillLeave(): void {
    this.clearReservedSort();
    if (this.submittedOrderVmSub$) {
      this.submittedOrderVmSub$?.unsubscribe();
    }
    if (this.submittedOrderVmPageLoadedSub$) {
      this.submittedOrderVmPageLoadedSub$?.unsubscribe();
    }
    if (this.getDeliveryDetails$) {
      this.getDeliveryDetails$?.unsubscribe();
    }
    if (this.dealsProductsExistSub$) {
      this.dealsProductsExistSub$.unsubscribe();
    }
    if (this.customerSub$) {
      this.customerSub$.unsubscribe();
    }

    this.clearOrderItemSearch();
  }

  editSubmittedOrder() {
    if (
      !this.submittedOrderVm ||
      !this.submittedOrderVm?.submittedOrderById ||
      !this.submittedOrderVm?.submittedOrderById?.orderHeader
    ) {
      return;
    }
    const fullOrderId =
      `${this.submittedOrderVm?.submittedOrderById?.orderHeader?.customerNumber}:` +
      `${this.submittedOrderVm?.submittedOrderById?.orderHeader?.departmentNumber}:` +
      `${this.submittedOrderVm?.submittedOrderById?.orderHeader?.orderId}`;

    if (this.submittedOrderVm?.isEditable) {
      this.orderService
        .shouldShowCartSyncingPopover$()
        .pipe(take(1))
        .subscribe(async showPopover => {
          if (showPopover) {
            await this.presentSyncingModal(fullOrderId);
          } else {
            this.routeToEditableOrder(fullOrderId);
          }
        });

      const analyticsOrderId = OrderAnalyticsService.getAnalyticsOrderId(
        this.submittedOrderVm?.submittedOrderById?.orderHeader,
      );
      this.orderSubmittedAnalyticsService.trackEditOrderButtonClick(
        this.submittedOrderVm?.submittedOrderById?.orderItems,
        this.submittedOrderVm?.products,
        analyticsOrderId,
        this.submittedOrderVm?.submittedOrderById?.orderHeader?.orderStatus,
      );

      this.orderSubmittedTracingService.traceEditOrderButtonClick(
        this.submittedOrderVm?.submittedOrderById?.orderHeader,
      );
    }
  }

  async presentSyncingModal(fullOrderId: string) {
    this.modalService.setModalOptions(
      this.platformService?.isTouch.value,
      OrderSyncingComponent,
      { customerSwitch: false, orderSwitch: true },
      'simple-action-modal',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    modal.onDidDismiss().then(resp => {
      if (resp?.data?.continueClick) {
        this.routeToEditableOrder(fullOrderId);
      }
    });
    return await modal.present();
  }

  routeToEditableOrder(fullOrderId: string) {
    this.myOrderStoreService
      ?.getOrderWithId(fullOrderId)
      ?.pipe(take(1))
      ?.subscribe(order => {
        this.routeTo(PATHS.ORDER);
        let customer: Customer;
        this.customer$?.pipe(take(1)).subscribe(cust => (customer = cust));
        this.orderService?.makeOrderCurrentOrder(order, customer);
      });
  }

  cancelOrder(resp: any) {
    if (resp?.data?.dispatchAction && this.submittedOrderVm?.isCancellable) {
      const currentOrder: Order = this.submittedOrderVm?.submittedOrderById;
      this.orderService?.cancelSubmittedOrder(currentOrder);
    }
  }

  confirmTransfer(resp: any) {
    if (resp?.data?.dispatchAction) {
      this.submittedOrderInfoCard.doPunchoutTransfer();
    }
  }

  transferModal = async () => {
    if (this.confirmModalOpen) return;

    const modalOptions = {
      title: this.translate?.instant('i18n.orderPage.confirmTransfer'),
      content: this.translate?.instant(
        'i18n.orderPage.confirmTransferSubtitle',
      ),
      exitButtonForTablet: true,
      exitButtonForMobile: true,
      withIcon: false,
      leftButtonText: this.translate?.instant('i18n.common.cancel'),
      rightButtonText: this.translate?.instant('i18n.common.confirm'),
    };

    const isTouchDevice = this.platformService.isTouch.value;
    this.modalService.setModalOptions(
      isTouchDevice,
      SimpleActionModalComponent,
      modalOptions,
      'simple-action-modal',
    );

    const modal = await this.modalController?.create(
      this.modalService.modalOptions,
    );
    modal?.onDidDismiss().then((resp: any) => {
      this.confirmTransfer(resp);
      this.confirmModalOpen = false;
    });

    this.confirmModalOpen = true;
    return await modal.present();
  };

  cancelOrderModal = async () => {
    if (!this.confirmModalOpen) {
      if (this.platformService.isTouch.value) {
        this.modalService.setModalOptions(
          true,
          SimpleActionModalComponent,
          {
            title: this.translate?.instant('i18n.orderPage.confirm'),
            content: this.translate?.instant('i18n.orderPage.confirmSubtitle'),
            note: this.translate?.instant('i18n.orderPage.confirmDescription'),
            exitButtonForTablet: true,
            exitButtonForMobile: true,
          },
          'simple-action-modal',
        );
      } else {
        this.modalService.setModalOptions(
          false,
          SimpleActionModalComponent,
          {
            title: this.translate?.instant('i18n.orderPage.confirm'),
            content: this.translate?.instant('i18n.orderPage.confirmSubtitle'),
            note: this.translate?.instant('i18n.orderPage.confirmDescription'),
            exitButtonForTablet: true,
            exitButtonForMobile: true,
          },
          'simple-action-modal',
        );
      }
      const modal = await this.modalController?.create(
        this.modalService.modalOptions,
      );
      modal?.onDidDismiss().then((resp: any) => {
        this.cancelOrder(resp);
        this.confirmModalOpen = false;
      });
      this.confirmModalOpen = true;
      return await modal.present();
    }
  };

  /* Can track items by identity when ngFor-of loop type is mismatched in the template*/
  trackById = (index: number, id: any) => id;
  trackByIndex = (index: number, id: any) => index;

  routeTo(path: string) {
    const url = window.location.pathname;
    this.router?.navigate([url.split('/')[1], path]);
  }

  loadMap() {
    try {
      this.isAllCoordinatesValid = this.validateCoordinates(
        this.submittedOrderVm?.submittedOrderById?.orderHeader
          ?.truckStopLatitude,
        this.submittedOrderVm?.submittedOrderById?.orderHeader
          ?.truckStopLongitude,
        this.submittedOrderVm?.submittedOrderById?.orderHeader
          ?.customerStopLatitude,
        this.submittedOrderVm?.submittedOrderById?.orderHeader
          ?.customerStopLongitude,
      );
      const loader = new Loader({
        apiKey: environment.googleMapsAPIKey,
      });
      loader?.importLibrary('maps').then(({ Map }) => {
        if (this.mapEl) {
          this.map = new Map(this.mapEl?.nativeElement, {
            center: { lat: 41.9741399, lng: -87.9481057 },
            zoom: 13,
            mapTypeControl: false,
            fullscreenControl: false,
            streetViewControl: false,
          });
          const truckMarker = new google.maps.Marker({
            position: {
              lat: +this.submittedOrderVm?.submittedOrderById?.orderHeader
                ?.truckStopLatitude,
              lng: +this.submittedOrderVm?.submittedOrderById?.orderHeader
                ?.truckStopLongitude,
            },
            map: this.map,
            icon: 'assets/icon/map-truck.svg',
          });
          const customerMarker = new google.maps.Marker({
            position: {
              lat: +this.submittedOrderVm?.submittedOrderById?.orderHeader
                ?.customerStopLatitude,
              lng: +this.submittedOrderVm?.submittedOrderById?.orderHeader
                ?.customerStopLongitude,
            },
            map: this.map,
            icon: 'assets/icon/map-pin.svg',
          });
          const markers = [truckMarker, customerMarker];
          const bounds = new google.maps.LatLngBounds();
          for (var i = 0; i < markers.length; i++) {
            bounds.extend(markers[i].getPosition());
          }
          setTimeout(() => {
            this.map?.fitBounds(bounds);
          }, 500);
        }
      });
    } catch {
      this.orderHasDeliveryDetails = false;
    }
  }

  validateCoordinates = (
    truckStopLatitude: any,
    truckStopLongitude: any,
    customerStopLatitude: any,
    customerStopLongitude: any,
  ): boolean => {
    const coordinates = [
      truckStopLatitude,
      truckStopLongitude,
      customerStopLatitude,
      customerStopLongitude,
    ].map(arg => {
      if (typeof arg === 'string') {
        const num = parseFloat(arg);
        return isNaN(num) ? null : num; // Return null for non-numeric strings
      }
      return arg;
    });

    const areAllNumbers = coordinates.every(
      arg => typeof arg === 'number' && arg !== null && arg !== undefined,
    );

    if (areAllNumbers) {
      // If all parameters are numbers, check if they are all 0
      return (
        !coordinates.every(num => num === 0) &&
        !coordinates.every(num => num === -1) &&
        this.isValidLatitude(truckStopLatitude) &&
        this.isValidLongitude(truckStopLongitude) &&
        this.isValidLatitude(customerStopLatitude) &&
        this.isValidLongitude(customerStopLongitude) &&
        (truckStopLatitude !== -1 || truckStopLongitude !== -1) &&
        (customerStopLatitude !== -1 || customerStopLongitude !== -1)
      );
    } else {
      return false;
    }
  };

  isValidLatitude = (latitude: number | null | undefined) => {
    //do not use !!latitude to check null or undefined for zero as it is a falsy value
    return (
      latitude !== null &&
      latitude !== undefined &&
      !isNaN(latitude) &&
      latitude >= -81 &&
      latitude <= 90
    );
  };

  isValidLongitude = (longitude: number | null | undefined) => {
    //do not use !!longitude to check null or undefined for zero as it is a falsy value
    return (
      longitude !== null &&
      longitude !== undefined &&
      !isNaN(longitude) &&
      longitude >= -178 &&
      longitude <= 178
    );
  };

  async openTrackingInfo(trackingInfo: ExternalTrackingInformation) {
    let url;
    switch (trackingInfo?.carrierCode?.toUpperCase()) {
      case 'UPS':
        url = environment.upsTrackingUrl?.replace(
          '{TRACKING_NUMBER}',
          trackingInfo?.trackingNumber,
        );
        break;
      case 'USPS':
        url = environment.uspsTrackingUrl?.replace(
          '{TRACKING_NUMBER}',
          trackingInfo?.trackingNumber,
        );
        break;
      case 'FEDEX':
        url = environment.fedexTrackingUrl?.replace(
          '{TRACKING_NUMBER}',
          trackingInfo?.trackingNumber,
        );
        break;
    }
    window.open(url, '_blank');
  }

  accordionGroupChange(ev: any, trackingGroup: any) {
    if (this.value === ev.detail.value) {
      this.expanded = true;
      this.closedPanels.splice(this.closedPanels.indexOf(trackingGroup));
    }
    if (this.value !== ev.detail.value) {
      this.expanded = false;
      this.closedPanels.push(trackingGroup);
    }
  }

  getDeliveryDetails() {
    const submittedOrderVm$ = of(this.submittedOrderVm);
    if (submittedOrderVm$) {
      this.getDeliveryDetails$ = submittedOrderVm$
        ?.pipe(
          withLatestFrom(this.customer$),
          filter(([vm, customer]) => !!vm?.submittedOrderById && !!customer),
        )
        ?.subscribe(([vm, customer]) => {
          if (customer && customer.timeZone) {
            this.customerTimeZone = customer?.timeZone;
          }
          const timezoneValidationFromOrder =
            vm?.submittedOrderById?.orderHeader?.timeZone === '-1' ||
            !vm?.submittedOrderById?.orderHeader?.timeZone
              ? 'CT'
              : vm?.submittedOrderById?.orderHeader?.timeZone;
          const finalTimeZone = this.isWmtTimezoneFeatureFlag
            ? timezoneValidationFromOrder
            : this.customerTimeZone;
          const tzCode = this.wmtService?.translateTZCode(finalTimeZone);
          if (this.hasETADetails) {
            this.deliveryETA = `${this.getTimeFormatted(
              new Date(
                new Date(
                  vm?.submittedOrderById?.orderHeader?.etaRangeMinimum,
                ).toLocaleString('en-US', {
                  timeZone: tzCode,
                }),
              ),
            )} - ${this.getTimeFormatted(
              new Date(
                new Date(
                  vm?.submittedOrderById?.orderHeader?.etaRangeMaximum,
                ).toLocaleString('en-US', {
                  timeZone: tzCode,
                }),
              ),
            )}`;
          }
          if (
            vm?.submittedOrderById?.orderHeader?.customerNumber &&
            vm?.submittedOrderById?.orderHeader?.truckStopLatitude &&
            vm?.submittedOrderById?.orderHeader?.truckStopLongitude &&
            vm?.submittedOrderById?.orderHeader?.customerStopLatitude &&
            vm?.submittedOrderById?.orderHeader?.customerStopLongitude
          ) {
            this.orderHasDeliveryDetails =
              !!vm?.submittedOrderById?.orderHeader?.tandemOrderNumber &&
              vm?.submittedOrderById?.orderHeader?.orderStatus !==
                OrderStatus.PICKING;
          } else {
            this.orderHasDeliveryDetails =
              vm?.submittedOrderById?.orderHeader?.deliveryEta !== '' ||
              vm?.submittedOrderById?.orderHeader?.deliveredDtm !== '';
            this.deliveryDetails = null;
          }

          if (
            this.orderHasDeliveryDetails &&
            (!vm?.submittedOrderById?.orderHeader?.orderDelivered ||
              vm?.submittedOrderById?.orderHeader?.orderDelivered === undefined)
          ) {
            this.loadMap();
            if (!this.hasETADetails) {
              this.deliveryETA = this.wmtService?.getDeliveryTime(
                vm?.submittedOrderById?.orderHeader?.deliveryEta,
                vm?.submittedOrderById?.orderHeader?.etaRangeMinimum,
                vm?.submittedOrderById?.orderHeader?.etaRangeMaximum,
                vm?.submittedOrderById?.orderHeader?.orderDelivered,
                true,
                this.customerTimeZone,
              );
            }
          } else if (
            this.orderHasDeliveryDetails &&
            vm?.submittedOrderById?.orderHeader?.orderDelivered
          ) {
            this.actualDeliveryDate = new Date(
              new Date(
                vm?.submittedOrderById?.orderHeader?.deliveredDtm,
              ).toLocaleString('en-US', {
                timeZone: tzCode,
              }),
            );
          }
        });
    }
  }

  get hasETADetails(): boolean {
    return (
      !!this.submittedOrderVm?.submittedOrderById?.orderHeader
        ?.etaRangeMaximum &&
      !!this.submittedOrderVm?.submittedOrderById?.orderHeader
        ?.etaRangeMinimum &&
      this.isValidDate(
        this.submittedOrderVm?.submittedOrderById?.orderHeader?.etaRangeMaximum,
      ) &&
      this.isValidDate(
        this.submittedOrderVm?.submittedOrderById?.orderHeader?.etaRangeMinimum,
      )
    );
  }

  get isWillCall(): boolean {
    return (
      this.submittedOrderVm?.submittedOrderById?.orderHeader?.orderType === 'WC'
    );
  }

  getTimeFormatted(etaDate: Date | string, isMobile?: boolean): string {
    const date = new Date(etaDate);
    const formatDate = (num: number) => {
      return num > 9 ? num : '0' + num;
    };
    let hours = date.getHours();
    const mins = date.getMinutes();
    let format = hours >= 12 && hours < 24 ? ' pm' : ' am';
    if (hours > 12) hours = hours - 12;
    if (isMobile) format = format.slice(0, -1);
    return `${formatDate(hours)}:${formatDate(mins)}${format}`;
  }

  showSubmittedOrderActionSheet(show: boolean) {
    this.showSubmittedOrderActionSheet$.next(show);
  }

  constructDownloadOrderButton() {
    const buttons = [];

    buttons.push({
      text: this.translate.instant('i18n.orderPage.print'),
      handler: () => {
        this.printOrderEmitterHandler();
      },
    });

    if (this.downloadButtonDisplayEnabled) {
      buttons.push({
        text: this.translate.instant('i18n.orderPage.download'),
        handler: () => {
          this.showDownloadOrderModal(true);
        },
      });
    }
    buttons.push({
      text: this.translate.instant('i18n.orderPage.share'),
      handler: () => {
        this.shareOrderEmitterHandler();
      },
    });

    buttons.push({
      text: this.translate.instant('i18n.common.cancel'),
      role: 'cancel',
      cssClass: 'cancel-button',
    });

    return buttons;
  }

  showDownloadOrderModal(isOpen: boolean) {
    this.showDownloadOrderModal$.next(isOpen);
  }

  showShipToModal(isOpen: boolean) {
    this.showShipToModal$.next(isOpen);
  }

  dismissShipToModal(event?: any): void {
    let currentSelections: ShipToModalSelections;
    currentSelections = event?.detail?.data?.currentSelections;
    if (!currentSelections) {
      return this.showShipToModal(false);
    }
    const expectedShipToCustomerNumber =
      event?.detail?.data?.currentSelections?.customer?.customerNumber;

    const order = { ...this.submittedOrderVm?.submittedOrderById };
    this.orderConversionService.submitShipToDetails(currentSelections, order);
    this.getSuperUserShipToCustomerIfNeeded(true, expectedShipToCustomerNumber);
    this.showShipToModal(false);
  }

  showRecoveryModal(isOpen: boolean) {
    this.showRecoveryModal$.next(isOpen);
  }

  dismissRecoveryModal(event?: any) {
    this.showRecoveryModal(false);
  }

  submitRecoveryOrder(event: RecoveryRequest) {
    this.serviceRequestService.submitRecoveryRequest(
      this.submittedOrderVm?.submittedOrderById,
      event,
    );
    this.showRecoveryModal(false);
  }

  handleDownloadOrder(downloadOrdersEvent: DownloadOrdersEvent) {
    let order: Order = { ...downloadOrdersEvent.orders[0] };
    let products = this.submittedOrderVm?.products;
    let unusedSubItems = [];
    if (!!order?.orderItems && !!this.submittedOrderVm?.autoSubItems) {
      this.submittedOrderVm.autoSubItems.forEach((subi, productNumber) => {
        if (!!subi?.orderItem && !!subi?.productSummary) {
          if (
            order.orderItems.findIndex(
              oi => oi.productNumber === subi.orderItem.productNumber,
            ) === -1
          ) {
            const subIndex = order.orderItems.findIndex(
              oi => oi.substituteFor === subi.orderItem.productNumber,
            );
            if (subIndex > -1) {
              order.orderItems.splice(subIndex, 0, subi.orderItem);
            }
          }
          products.set(subi.productSummary.productNumber, {
            summary: subi.productSummary,
            contract: null,
            inventory: null,
          } as Product);
        } else {
          unusedSubItems.push(productNumber);
        }
      });

      unusedSubItems.forEach(productNumber => {
        const oIdx = order.orderItems.findIndex(
          oi => oi.substituteFor === productNumber,
        );
        if (oIdx > -1) {
          const oiCopy = { ...order.orderItems[oIdx] };
          oiCopy.substituteFlag = '';
          oiCopy.substituteFor = undefined;
          order.orderItems.splice(oIdx, 1, oiCopy);
        }
      });
    }

    this.orderDownloadService
      .downloadAndOpenOrder([order], {
        products,
        fileName: downloadOrdersEvent.fileName,
        fileType: downloadOrdersEvent.fileType,
      })
      .then(() => {
        if (
          !downloadOrdersEvent.fileName ||
          !downloadOrdersEvent.fileType.fileExtension
        ) {
          return;
        }
        this.orderSubmittedAnalyticsService.trackDownloadOrder(
          this.submittedOrderVm?.submittedOrderById?.orderHeader?.orderId,
          this.submittedOrderVm?.submittedOrderById?.orderHeader?.orderStatus,
          `download:order:${downloadOrdersEvent.fileType?.fileExtension?.toString().toLowerCase()}`,
        );

        this.orderSubmittedTracingService.traceSubmittedOrderDownload(
          this.submittedOrderVm?.submittedOrderById?.orderHeader,
          downloadOrdersEvent.fileType,
        );
      });
  }

  showSortModal(isOpen: boolean) {
    this.showSortModal$.next(isOpen);
  }

  showWillCallModal(isOpen: boolean) {
    this.showWillCallModal$.next(isOpen);
  }

  openWillCallModal() {
    this.showWillCallModal(true);

    if (
      this.submittedOrderVm?.submittedOrderById?.orderHeader?.orderType === 'WC'
    ) {
      this.convertToWillCallTracingService.traceEditConvertToWillCallClick(
        this.submittedOrderVm?.submittedOrderById?.orderHeader,
        this.submittedOrderVm?.submittedOrderById?.orderItems,
        this.submittedOrderVm?.products,
      );
    } else {
      this.convertToWillCallTracingService.traceConvertToWillCallClick(
        this.submittedOrderVm?.submittedOrderById?.orderHeader,
        this.submittedOrderVm?.submittedOrderById?.orderItems,
        this.submittedOrderVm?.products,
      );
    }

    this.convertToWillCallAnalyticsService.trackWillCallModalOpen(
      this.submittedOrderVm?.submittedOrderById?.orderHeader?.orderId,
      this.submittedOrderVm?.submittedOrderById?.orderHeader?.divisionNumber,
      this.submittedOrderVm?.submittedOrderById?.orderItems,
      this.submittedOrderVm?.products,
    );
  }

  cancelWillCallModal(willCallEvent?: WillCallEvent) {
    this.convertToWillCallTracingService.traceWillCallFormModalCancelClick(
      this.submittedOrderVm?.submittedOrderById?.orderHeader,
      willCallEvent?.willCallDateTime,
      willCallEvent?.willCallPersonName,
    );
    this.convertToWillCallAnalyticsService.trackWillCallModalClose(
      this.submittedOrderVm?.submittedOrderById?.orderHeader?.orderId,
      this.submittedOrderVm?.submittedOrderById?.orderHeader?.divisionNumber,
      this.submittedOrderVm?.submittedOrderById?.orderItems,
      this.submittedOrderVm?.products,
    );

    this.showWillCallModal(false);
  }

  dismissWillCallModal(_event?: any) {
    this.convertToWillCallAnalyticsService.trackWillCallModalClose(
      this.submittedOrderVm?.submittedOrderById?.orderHeader?.orderId,
      this.submittedOrderVm?.submittedOrderById?.orderHeader?.divisionNumber,
      this.submittedOrderVm?.submittedOrderById?.orderItems,
      this.submittedOrderVm?.products,
    );

    this.showWillCallModal(false);
  }

  async confirmWillCall(willCallEvent: WillCallEvent) {
    this.showWillCallModal(false);

    if (
      this.submittedOrderVm?.submittedOrderById?.orderHeader?.orderType === 'WC'
    ) {
      this.submitWillCall(willCallEvent);
    } else {
      this.modalService.setModalOptions(
        this.platformService.isTouch.value,
        WillCallConfirmModalComponent,
        {
          isOnline: this.panAppState.online$,
        },
        'modal-desktop-size-md',
      );
      const modal = await this.modalController.create(
        this.modalService.modalOptions,
      );
      modal.onDidDismiss().then((resp: any) => {
        let newOrderHeaderForTracing: OrderHeader = {
          ...this.submittedOrderVm?.submittedOrderById?.orderHeader,
          ...willCallEvent,
        };

        if (resp.data?.dispatchAction) {
          this.convertToWillCallAnalyticsService.trackSubmitConvertToWillCallRequest(
            this.submittedOrderVm?.submittedOrderById?.orderHeader?.orderId,
            this.submittedOrderVm?.submittedOrderById?.orderHeader
              ?.divisionNumber,
            this.submittedOrderVm?.submittedOrderById?.orderItems,
            this.submittedOrderVm?.products,
          );
          this.convertToWillCallTracingService.traceSubmitWillCallFormModalDismiss(
            newOrderHeaderForTracing,
            this.submittedOrderVm?.submittedOrderById?.orderItems,
            this.submittedOrderVm?.products,
          );
          this.submitWillCall(willCallEvent);
        } else {
          this.convertToWillCallTracingService.traceWillCallConfirmationModalCancelClick(
            this.submittedOrderVm?.submittedOrderById?.orderHeader,
            willCallEvent?.willCallDateTime,
            willCallEvent?.willCallPersonName,
          );
        }
      });

      await modal.present();
    }
  }

  async submitWillCall(willCallEvent: any) {
    const newOrderHeader: OrderHeader = {
      ...this.submittedOrderVm?.submittedOrderById?.orderHeader,
      updateDtm: new Date().toISOString(),
      willCallPersonName: willCallEvent?.willCallPersonName,
      willCallSpecialInstructions: willCallEvent?.willCallSpecialInstructions,
      willCallDateTime: willCallEvent?.willCallDateTime,
      willCallType: willCallEvent?.willCallType,
    };

    const isEditWillCallFlow =
      this.submittedOrderVm?.submittedOrderById?.orderHeader?.orderType ===
      'WC';

    this.orderService.convertToWillCall(
      newOrderHeader,
      this.submittedOrderVm?.submittedOrderById?.orderItems,
      this.submittedOrderVm?.products,
      isEditWillCallFlow,
    );
  }

  getSuperUserShipToCustomerIfNeeded(
    isShipToUpdate: boolean,
    expectedShipToCustomerNumber?: number,
  ): void {
    if (isShipToUpdate) {
      this.hasRetrievedShipToCustomer = false;
    }
    combineLatest([this.isSuperUser$(), this.submittedOrderVm$])
      .pipe(
        filter(
          ([isSuperUser, vm]) =>
            !vm.shipToCustomer &&
            !this.hasRetrievedShipToCustomer &&
            (isShipToUpdate
              ? isSuperUser &&
                expectedShipToCustomerNumber ===
                  vm?.submittedOrderById?.orderHeader?.shipToCustomer
              : !!vm?.submittedOrderById?.orderHeader?.shipToCustomer &&
                vm?.submittedOrderById?.orderHeader?.shipToCustomer !==
                  vm?.submittedOrderById?.orderHeader?.customerNumber),
        ),
        take(1),
      )
      .subscribe(([_, vm]) => {
        this.superUserShipToCustomer$ = this?.orderConversionService
          .getCustomerForSuperUser$(
            vm?.submittedOrderById?.orderHeader?.shipToCustomer * 1,
            vm?.selectedCustomer?.divisionNumber * 1, //ship to customer will always have same division as current customer
          )
          .pipe(
            filter(
              cust =>
                cust?.customerNumber ===
                vm?.submittedOrderById?.orderHeader?.shipToCustomer,
            ),
            tap(cust => {
              this.superUserShipToCustomer = cust;
              this.isSuperUserProntoCustomer =
                this.superUserShipToCustomer?.prontoEnabledFlag ===
                PRONTO_KEYS.PRONTO;
            }),
          );
        this.hasRetrievedShipToCustomer = true;
      });
  }

  closeSortModal(event: any) {
    const sortDirection: SortDirections = event?.detail?.data?.sortDirection;
    if (event?.detail?.data?.sortOrClear) {
      this.applyFilter = event?.detail?.data?.filterSelection;
      this.sortByReserved.next(sortDirection);
      this.filterSelection.next(event?.detail?.data?.filterSelection);
      this.sortFilterCount = event?.detail?.data?.sortFilterCount;
    }
    this.showSortModal$.next(false);
  }

  clearReservedSort() {
    this.applyFilter = false;
    this.sortFilterCount = 0;
    this.filterSelection.next(false);
    this.sortByReserved.next(null);
  }

  filterOrderItemsByUnreserved(orderItems: OrderItem[]): OrderItem[] {
    return orderItems.filter(product => {
      if (
        product?.eachesOrdered?.currentValue !== product.eachesReserved &&
        product?.unitsOrdered?.currentValue !== product.unitsReserved
      ) {
        return true;
      } else {
        return false;
      }
    });
  }

  //First, sort by orderItem.unitsReserved.
  //If unitsReserved are equal, sort by eachesReserved.
  sortOrderItemsByReserved(
    orderItems: OrderItem[],
    sortDirection: SortDirections,
  ): OrderItem[] {
    return orderItems.sort((a, b) => {
      if (sortDirection === SortDirections.asc) {
        return a.unitsReserved !== b.unitsReserved
          ? a.unitsReserved - b.unitsReserved
          : a.eachesReserved - b.eachesReserved;
      } else if (sortDirection === SortDirections.desc) {
        return b.unitsReserved !== a.unitsReserved
          ? b.unitsReserved - a.unitsReserved
          : b.eachesReserved - a.eachesReserved;
      } else {
        return;
      }
    });
  }

  loadCustomersByDivision(): Observable<Customer[]> {
    return this.orderService.loadCustomersByDivisionNumber$(
      this.submittedOrderVm?.submittedOrderById?.orderHeader?.divisionNumber,
    );
  }

  isSuperUser$(): Observable<boolean> {
    return this.orderService.isSuperUser$();
  }

  autoSubItem(subbedProductNumber: number): OrderItem {
    if (
      !this.autosubDisplayEnabled ||
      !this.submittedOrderVm?.selectedCustomer?.autoSubProducts
    ) {
      return;
    }
    return this.submittedOrderVm?.autoSubItems?.get(subbedProductNumber)
      ?.orderItem;
  }

  printOrderEmitterHandler() {
    this.orderService
      .loadOrderMinimumViewModel$(
        this.submittedOrderVm?.submittedOrderById?.orderHeader,
      )
      .pipe(take(1))
      .subscribe(orderMinimum => {
        const shipToCustomer =
          this.submittedOrderVm?.shipToCustomer || this.superUserShipToCustomer;

        const shipToDepartmentName =
          this.submittedOrderVm?.shipToDepartmentName ||
          this.getShipToDepartmentName(
            this.superUserShipToCustomer,
            this.submittedOrderVm?.submittedOrderById?.orderHeader
              ?.shipToDepartment,
          );

        this.orderService.printSubmittedOrder(
          [this.submittedOrderVm?.submittedOrderById],
          false,
          this.submittedOrderVm?.tallies,
          orderMinimum,
          shipToCustomer,
          shipToDepartmentName,
          this.submittedOrderVm?.orderStatusDetails,
        );
      });
  }

  shareOrderEmitterHandler() {
    this.orderService.shareSubmittedOrder([
      this.submittedOrderVm?.submittedOrderById,
    ]);
  }

  setIsInternalUser(): void {
    this.panAppState.accessToken$
      .pipe(
        filter(token => !!token),
        take(1),
      )
      .subscribe(token => {
        const decoded = jwtDecode(token);
        if (decoded) {
          this.isInternalUser = decoded?.['usf-claims']?.userType === 'corp-ad';
        }
      });
  }

  isValidDate(dateToValidate: any): boolean {
    try {
      if (new Date(dateToValidate).toString() === 'Invalid Date') return false;
    } catch (e) {
      return false;
    }
    return true;
  }

  loadOrderRefresherData(orderId: string) {
    const values = orderId ? orderId.split(':') : [];
    const customerNumber = parseInt(values[0]) ?? undefined;
    const orderServerId = values[2] ?? undefined;

    this.orderService.checkCustomerIsAvailable(customerNumber);
    this.orderService.checkOrderStatus(orderServerId);
    this.store
      .select(orderSelectors.selectOrderStatusInFlight())
      .pipe(
        filter(fetchingOrder => !!fetchingOrder),
        take(1),
      )
      .subscribe(() => {
        this.selectOrderData(orderId);
      });
  }

  selectOrderData(orderId: string) {
    this.store
      .select(orderSelectors.selectOrderStatusInFlight())
      .pipe(
        filter(fetchingOrder => !fetchingOrder),
        take(1),
      )
      .subscribe(() => {
        this.panAppState.currentUrl$.pipe(take(1)).subscribe(url => {
          if (orderId) {
            combineLatest([
              this.myOrderStoreService.getOrderWithId(orderId),
              this.panAppState.user$,
            ])
              .pipe(
                filter(([order, user]) => !!user),
                take(1),
              )
              .subscribe(([order, user]) => {
                if (order && this.orderService.orderStatusOnWhitelist(order)) {
                  this.loadProductsForOrder(order, user);
                } else {
                  this.orderService.handleOrderDetailsPageError();
                }
              });
          } else {
            this.orderService.handleOrderDetailsPageError();
          }
        });
      });
  }

  loadProductsForOrder(order: Order, user: UserState) {
    if (user.tmUser === 'Y' && order?.orderHeader?.orderType !== 'VS') {
      this.messageHandlerService
        .createSpinnerModal(true, PATHS.MY_ORDERS_VIEW)
        .then(() =>
          this.myOrderStoreService.retrieveOrderDetails(
            order?.orderHeader.tandemOrderNumber,
            false,
          ),
        );
    } else {
      this.orderService.loadProductsForOrder(order);
    }
  }

  handleRefresh(event: any) {
    if (!this.orderService.networkStatus) {
      (event?.target as any)?.complete();
    } else {
      this.loadOrderRefresherData(this.selectedSubmittedOrderId);
      setTimeout(() => {
        (event?.target as any)?.complete();
      }, 250);
    }
  }

  orderItemSearch(event: any) {
    this.searchKey.next(event.target.value);
  }

  clearOrderItemSearch() {
    this.searchKey.next('');
  }

  getShipToDepartmentName(shipToCustomer: Customer, shipToDepartment): string {
    return shipToCustomer?.departments
      ?.find(dept => Number(dept?.departmentNumber) === shipToDepartment * 1)
      ?.departmentName.toUpperCase();
  }

  async setShowEmail() {
    this.showEmail$ = this.panAppState.user$.pipe(map(user => !user.email));
  }

  checkForProntoOrder() {
    this.orderService
      .selectedCustomerProntoDeliveryDays()
      .pipe(take(1))
      .subscribe(selectedDays => {
        this.isProntoDeliveryDate = isProntoDay(
          selectedDays,
          this.submittedOrderVm?.submittedOrderById?.orderHeader?.confirmedDeliveryDate?.toString(),
        );
      });
  }
}
